import { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { FEATURE_FLAGS_NAMES } from '@linearb/linjs-common';
import useSettingsService from 'components/hooks/useSettingsService';
import {
  teamsFlatTreeSelector,
  selectedTeamWithExtraDataSelector,
  syncTeamsRootLevelTreeSelector,
  getRootTeamSelector
} from 'state/selectors/teams';
import {
  selectedTeamSelector,
  selectedTeamChangedFromTopBarSelector,
  dateRangeSelector
} from 'state/selectors/ui-state';
import { ALL_TEAMS_OPTION } from './views/CustomReport/Filters/TeamsDropdown';
import BrowserStorageAccessor from 'common/utils/BrowserStorageAccessor';
import {
  SESSION_STORAGE_KEYS,
  SEARCH_DATE_FORMAT
} from 'common/constants/constants';
import { FILTER_BY } from './views/CustomReport/consts';
import useSyncedQSFilters from 'components/hooks/useSyncedQSFilters';
import { filtersDefaultsMap } from './filtersToQs.map';
import { contributorsByIdSelector } from 'state/selectors/contributors';
import { flattenTree } from 'components/base/MultiLevelDropdown/MultiLevelDropdown.utils';
import useGitTeamSettings from 'views/root/views/home/views/settings/views/GitSettings/GitTeamSettings/useGitTeamSettings';
import { isTeam } from 'common/utils/utils';

const useFilters = ({ isTeamGoals = false } = {}) => {
  const selectedTeam = useSelector(selectedTeamSelector);
  const treeTeamsFromRoot = useSelector(syncTeamsRootLevelTreeSelector);
  const teamsFlatTree = useSelector(teamsFlatTreeSelector);
  const selectedTeamTree = useSelector(selectedTeamWithExtraDataSelector);
  const flatSelectedTeamTree = flattenTree(selectedTeamTree, 'children');
  const selectedTeamTreeIds = flatSelectedTeamTree
    .map(team => team?.id)
    .filter(Boolean);

  const { getSettingOrDefinitionValue } = useSettingsService();

  const isTeamGitSettingEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.isTeamGitSettingEnabled
  });

  const rootTeamId = useSelector(getRootTeamSelector);
  const contributors = useSelector(contributorsByIdSelector);
  const selectedTeamChangedFromTopBar = useSelector(
    selectedTeamChangedFromTopBarSelector
  );
  const dateRange = useSelector(dateRangeSelector);
  const isFutureDate = moment(dateRange?.to).isAfter(moment());
  const dateRangeTo = isFutureDate
    ? moment().format(SEARCH_DATE_FORMAT)
    : dateRange?.to;

  const selectedGlobalDateRange = {
    start_date: dateRange?.from,
    end_date: dateRangeTo
  };

  const gitTeamSettings = useGitTeamSettings({ teamId: selectedTeam?.id });
  const { reposIds = [], servicesIds = [] } = gitTeamSettings;

  const sessionReport = JSON.parse(
    BrowserStorageAccessor().session.get(
      SESSION_STORAGE_KEYS.SELECTED_DASHBOARDS
    ) || '{}'
  );

  // noFilters = Default Reports
  const noFilters = _.isEmpty(sessionReport?.definition?.filters);
  const teamHasGitScope =
    isTeam(selectedTeam) && (reposIds?.length > 0 || servicesIds?.length > 0);
  const [filtersMap, setFilterMap] = useState(
    filtersDefaultsMap({
      selectedTeam,
      treeTeamsFromRoot,
      contributors,
      selectedGlobalDateRange,
      ...(isTeamGoals
        ? {}
        : { defaultReposIds: reposIds, defaultServicesIds: servicesIds })
    })
  );

  const filterByRootTeamId = rootTeamId;

  const {
    isFilterSynced,
    syncedFilters: {
      teams,
      globallySelectedTeams,
      selectedRepo,
      selectedLabels,
      selectedContributor,
      selectedService,
      filterType,
      selectedGranularity,
      selectedTimeRanges
    },
    setters: {
      setGloballySelectedTeams,
      setSelectedRepo,
      setSelectedLabels,
      setSelectedContributor,
      setSelectedService,
      setFilterType,
      setSelectedGranularity,
      setSelectedTimeRanges
    }
  } = useSyncedQSFilters(filtersMap);

  const isFilterSyncedRef = useRef(null);
  const reportRef = useRef(null);

  useEffect(() => {
    setFilterMap(
      filtersDefaultsMap({
        selectedTeam,
        treeTeamsFromRoot,
        contributors
      })
    );
  }, [selectedTeam, treeTeamsFromRoot, contributors]);

  useEffect(() => {
    isFilterSyncedRef.current = isFilterSynced;
  }, [isFilterSynced]);

  const firstGloballySelectedTeamId = globallySelectedTeams?.[0]?.id;

  // Filters
  const filtersSnapshot = {
    globallySelectedTeams,
    selectedRepo,
    selectedLabels,
    selectedContributor,
    selectedService,
    filterType,
    selectedGranularity
  };

  useEffect(() => {
    if (selectedTeamChangedFromTopBar) {
      const team = teamsFlatTree.find(({ id }) => id === teams);
      setGloballySelectedTeams([team || ALL_TEAMS_OPTION.value]);
      setSelectedContributor(null);

      if (isTeamGitSettingEnabled && noFilters && !isTeamGoals) {
        setFilterType(FILTER_BY.PEOPLE);
        if (isTeam(selectedTeam)) {
          setSelectedRepo(reposIds);
          setSelectedService(servicesIds);
        } else {
          setSelectedRepo([]);
          setSelectedService([]);
          setSelectedLabels([]);
        }
      }
    }
  }, [teams]);

  let defaultSelectedTeam = treeTeamsFromRoot;

  if (!filterByRootTeamId) {
    defaultSelectedTeam = selectedTeam
      ? [selectedTeam]
      : [ALL_TEAMS_OPTION.value];
  }

  const filterTypeByReportDefinitionFilters = reportFilters => {
    const {
      selectedLabels = [],
      selectedRepo = [],
      selectedService = []
    } = reportFilters;

    if (selectedLabels.length > 0) {
      return FILTER_BY.LABEL;
    }

    if (selectedRepo?.length > 0) {
      return FILTER_BY.REPO;
    }

    if (selectedService?.length > 0) {
      return FILTER_BY.SERVICE;
    }

    return FILTER_BY.PEOPLE;
  };

  const setFiltersSnapshot = (snapshot = {}, report, teamHasGitScope) => {
    if (
      _.isEmpty(snapshot) ||
      isFilterSyncedRef.current ||
      // On init Metrics, if default report and team has git scope - don't set filters from session
      // (filters set by default from git scope)
      (!reportRef.current && report?.is_default && teamHasGitScope)
    ) {
      return;
    }
    setGloballySelectedTeams(
      !_.isEmpty(snapshot.globallySelectedTeams)
        ? snapshot.globallySelectedTeams
        : defaultSelectedTeam
    );
    setSelectedRepo(snapshot.selectedRepo || []);
    setSelectedLabels(snapshot.selectedLabels || []);
    setSelectedContributor(snapshot.selectedContributor || null);
    setSelectedService(snapshot.selectedService || []);
    setFilterType(snapshot.filterType || FILTER_BY.PEOPLE);
    setSelectedGranularity(snapshot.selectedGranularity || selectedGranularity);
  };

  const saveFiltersOnStorage = () => {
    BrowserStorageAccessor().session.set(
      SESSION_STORAGE_KEYS.METRICS_FILTERS,
      JSON.stringify(filtersSnapshot)
    );
  };

  // called when entering Default Reports. get filters from session
  const restoreFiltersFromStorage = ({ isTeamGoals, report } = {}) => {
    const sessionFilters = JSON.parse(
      BrowserStorageAccessor().session.get(
        SESSION_STORAGE_KEYS.METRICS_FILTERS
      ) || '{}'
    );

    const defaultSelectedRepo = sessionFilters?.selectedRepo;
    const defaultSelectedService = sessionFilters?.selectedService;

    const teamGoalsFilters = {
      filterType: null,
      selectedContributor: null,
      selectedLabels: [],
      selectedRepos: [],
      selectedService: []
    };
    setTimeout(() => {
      const globallySelectedTeam = _.first(globallySelectedTeams);
      const isGloballySelectedTeamInTree =
        !selectedTeam || selectedTeamTreeIds.includes(globallySelectedTeam?.id); // !selectedTeam - all teams
      const teamGoalsSelectedTeam = isGloballySelectedTeamInTree
        ? globallySelectedTeam
        : selectedTeam;
      setFiltersSnapshot(
        {
          ...sessionFilters,
          // Since repo and serivce used to be a single object
          // and now we are supporting multi,
          // we need to handle old behavior
          selectedRepo:
            defaultSelectedRepo !== null &&
            defaultSelectedRepo?.constructor?.name === 'Object'
              ? [defaultSelectedRepo?.id]
              : defaultSelectedRepo,
          selectedService:
            defaultSelectedService !== null &&
            defaultSelectedService?.constructor?.name === 'Object'
              ? [defaultSelectedService?.id]
              : defaultSelectedService,
          globallySelectedTeams: isTeamGoals
            ? [teamGoalsSelectedTeam]
            : sessionFilters.globallySelectedTeams,
          ...(isTeamGoals ? teamGoalsFilters : {})
        },
        report,
        teamHasGitScope
      );
      reportRef.current = report;
    }, 2);
  };

  // called when entering Custom Reports - get filters from definition
  const initCustomReportFilters = report => {
    const reportFilters = report.definition.filters;
    const filterType = filterTypeByReportDefinitionFilters(reportFilters);
    setTimeout(() => {
      setFiltersSnapshot({ ...reportFilters, filterType }, report, false);
      reportRef.current = report;
    }, 2);
  };

  return {
    globallySelectedTeams,
    firstGloballySelectedTeamId,
    selectedTimeRanges,
    selectedContributor,
    selectedGranularity,
    selectedRepo,
    selectedLabels,
    selectedService,
    filterType,
    saveFiltersOnStorage,
    restoreFiltersFromStorage,
    initCustomReportFilters,
    defaultSelectedTeam,
    setGloballySelectedTeams,
    setSelectedRepo,
    setSelectedLabels,
    setSelectedContributor,
    setSelectedService,
    setFilterType,
    setSelectedGranularity,
    setSelectedTimeRanges
  };
};

export default useFilters;
